/* eslint-disable import/no-cycle, import/named, react/display-name */
import {
  PageInfo,
  ReplyEdgeNodeReliesFieldsFragment as Replies,
  UserFieldsFragment as ReplyListUser,
} from '__generated__/graphql/legacy/graphql';
import classNames from 'classnames/bind';
import { features } from 'constants/featureFlags';
import { RelevantMention } from 'interfaces/mentions';
import { SkeletonCommentContainer } from 'koba/components/SkeletonComment';
import { TFunction } from 'next-i18next';
import { COMMENTS_PER_PAGE, NUMBER_OF_REACTIONS } from 'utils/constants';

import React, { useState } from 'react';

import { useLazyQuery } from '@apollo/client';

import { Button } from '@thinkific/chalk/Button';

import Comment from 'components/Comment';
import { useCommunityContext } from 'components/CommunityContext';

import { NESTED_REPLIES_QUERY } from 'schema/Replies/queries';

import styles from './ReplyThread.module.scss';

const cx = classNames.bind(styles);

const replyList = (
  parentPostId,
  parentReplyId,
  replies,
  t,
  isFollowingThread,
  onPostChange,
  isReadOnlySpace = false
) => {
  return replies?.edges?.map(({ node }) => {
    return (
      <Comment
        comment={node}
        isFollowingThread={isFollowingThread}
        isReadOnlySpace={isReadOnlySpace}
        key={node.id}
        parentPostId={parentPostId}
        parentReplyId={parentReplyId}
        t={t}
        user={node.user}
        isReply
        onPostChange={onPostChange}
      />
    );
  });
};

interface ReplyThreadProps {
  author?: ReplyListUser;
  fetchMoreReplies?: () => void;
  hasReplies: boolean;
  isFollowingThread?: boolean;
  parentPostId: string;
  parentReplyId: string;
  replies: Replies;
  t: TFunction;
  onPostChange: () => void;
  isReadOnlySpace?: boolean;
}

export const ReplyThread: React.FC<ReplyThreadProps> = ({
  author = {},
  hasReplies,
  isFollowingThread = false,
  replies,
  parentPostId,
  parentReplyId,
  t,
  onPostChange,
  fetchMoreReplies,
  isReadOnlySpace = false,
}) => {
  const [hasFetchedMore, setHasFetchedMore] = useState(false);
  const { community, featureFlags, currentUser: me } = useCommunityContext();
  const multipleReactionsEnabled = featureFlags[features.COMMUNITY_REACTIONS] || false;

  let relevantMentions: RelevantMention[] = [];

  if (replies.totalCount > 0) {
    replies?.edges?.forEach((edge) => {
      const node = edge?.node;
      relevantMentions.push({
        display: node?.user?.fullName || '',
        id: node?.user?.id || '',
        avatarUrl: node?.user?.avatarUrl || '',
      });
    });

    if (author.id && author.fullName) {
      relevantMentions.push({
        display: author?.fullName,
        id: author?.id,
        avatarUrl: author?.avatarUrl || '',
      });
    }

    relevantMentions = relevantMentions.filter((user) => user.id !== me.id);
  }

  let replyThreadDataLength;
  let pageInfo: PageInfo = {
    __typename: 'PageInfo',
    endCursor: null,
    hasNextPage: false,
    hasPreviousPage: false,
    startCursor: null,
  };

  if (hasReplies && replies) {
    pageInfo = replies.pageInfo;
    replyThreadDataLength = Math.min(
      replies.totalCount - (replies?.edges?.length || 0),
      COMMENTS_PER_PAGE
    );
  }

  const [fetchInitialReplies, { loading, fetchMore }] = useLazyQuery(NESTED_REPLIES_QUERY, {
    variables: {
      communityId: community.id,
      parentReplyId,
      startCursor: pageInfo.startCursor,
      numOfReplies: COMMENTS_PER_PAGE,
      numberOfReactions: NUMBER_OF_REACTIONS,
      multipleReactionsEnabled,
    },
    notifyOnNetworkStatusChange: true,
    onCompleted: () => {
      // apollographql/apollo-client#6636 - onCompleted does not fire again, requires use of fetchMore
      setHasFetchedMore(true);
    },
  });

  const handleFetchMore = () => {
    fetchMore?.({
      variables: {
        startCursor: pageInfo.startCursor,
        numOfReplies: COMMENTS_PER_PAGE,
      },
    });
  };

  return (
    <div
      className={cx('comment-list', 'mentions-enabled-reply-thread')}
      data-qa="comment-reply-section"
    >
      {loading && <SkeletonCommentContainer />}
      {!loading && pageInfo.hasPreviousPage && (
        <div className={cx('comment-list-button')}>
          <Button
            className={cx('comment-list-button__previous-comments')}
            emphasis="low"
            onClick={() => {
              if (fetchMoreReplies) {
                // Parent components handling fetch more
                // We could refactor CommentList.js to handle the fetch more in the same way to be consistent
                fetchMoreReplies();
              } else if (hasFetchedMore) {
                handleFetchMore();
              } else {
                fetchInitialReplies();
              }
            }}
          >
            {t('View previous {{portion}} replies', {
              portion: replyThreadDataLength,
            })}
          </Button>
        </div>
      )}
      {replies &&
        replyList(
          parentPostId,
          parentReplyId,
          replies,
          t,
          isFollowingThread,
          onPostChange,
          isReadOnlySpace
        )}
    </div>
  );
};

export default ReplyThread;
